import React, { Fragment } from 'react';
import cn from 'classnames';
import { format } from 'date-fns';
import utcToZonedDate from '@framework/date-helpers/utcToZonedDate';
import { MONTH_DAY_YEAR_FORMAT } from '@dealroadshow/uikit-date-time/dateFormats';
import IconNewWindow from '@creditflowresearch/uilib/Icon/IconNewWindow';
import ITranch from '@/creditflowresearch/domain/vo/Tranch/ITranch';
import IDeal, { IDealFiling } from '@/creditflowresearch/domain/vo/IDeal';
import { ICorporateDeal } from '@/creditflowresearch/domain/vo/Deals/CorporateDeal';
import {
  IAbsActiveCalendarDealDetails,
  IAbsActiveCalendarTrancheView,
} from '@/creditflowresearch/application/ActiveCalendar/Abs/vo/IAbsActiveCalendarViewItem';

import styles from '@/creditflowresearch/ui/components/deals/DealListTable/components/DealBody/DealDetails/dealDetails.module.scss';

const allAvailableDealProperties = [
  // ABS section
  { name: 'Cut-Off Balance ($mm)', field: 'cutOffBalance' },
  { name: 'Expected Pricing Date', field: 'expectedPricingDate' },
  { name: 'Expected Ratings Date', field: 'expectedRatingsDate' },
  { name: 'Expected Settlement Date', field: 'expectedSettlementDate' },
  { name: 'Pricing Speed', field: 'pricingSpeed' },
  // CMBS section
  { name: '# of Loans', field: 'numberOfLoans' },
  { name: '# of Properties', field: 'numberOfProperties' },
  { name: 'Top 10 Loans %', field: 'top10Loans' },
  { name: 'WA Cut-Off LTV', field: 'waCutOffLtv' },
  { name: 'WA Maturity LTV', field: 'waMaturityLtv' },
  { name: 'WA Rem. Amrt Term (Months)', field: 'waRemAmrtTermMonths' },
  { name: 'WA Seasoning (Months)', field: 'waSeasoningMonths' },
  { name: 'WA Term to Maturity (Months)', field: 'waTermToMaturityMonths' },
  { name: 'WA U/W DSCR', field: 'waUWDscr' },
  { name: 'WA U/W NOI Debt Yield', field: 'waUWNoiDebtYield' },
  { name: 'Reinvestment Period (YRS)', field: 'reinvestmentPeriod' },
  { name: 'Reinvestment Period End Date', field: 'reinvestmentPeriodEndDate', dateTimeObject: true },
  { name: 'Refinancing', field: 'refinancing', isBoolean: true },
  { name: 'Reset', field: 'reset', isBoolean: true },
  { name: 'WAL Test Period (YRS)', field: 'walTestPeriod' },
  { name: 'WAL Test Period End Date', field: 'walTestEndDate', dateTimeObject: true },
];

export const getDealPropertyGroups = (deal: IDeal | ICorporateDeal | IAbsActiveCalendarDealDetails) => {
  const mapPropValue = (prop: any) => {
    if (prop.dateTimeObject && deal[prop.field] && typeof deal[prop.field] !== 'string') {
      const { timezone, dateTime } = deal[prop.field];
      return {
        ...prop,
        value: format(utcToZonedDate(timezone.timeZone, new Date(dateTime)), MONTH_DAY_YEAR_FORMAT),
      };
    }

    return {
      ...prop,
      // eslint-disable-next-line no-nested-ternary
      value: (prop.isBoolean === undefined)
          ? deal[prop.field]
          : (deal[prop.field] === 1)
              ? 'Yes'
              : null,
    };
  };

  // filter empty/no value elements
  let properties = allAvailableDealProperties
      .map(mapPropValue)
      .filter((dealProperty) => !!dealProperty.value);

  if (!properties.length) {
    return [];
  }

  const ITEMS_PER_COLUMN = 7;

  let columnsCount = Math.ceil(properties.length / ITEMS_PER_COLUMN);
  let propertyGroups = [];

  for (let i = 0; i < columnsCount; i++) {
    propertyGroups.push(properties.slice(i * ITEMS_PER_COLUMN, ITEMS_PER_COLUMN * (i + 1)));
  }

  return propertyGroups;
};

export const getDealFilings = (deal: IDeal | ICorporateDeal | IAbsActiveCalendarDealDetails) => {
  if (!deal.filings || !deal.filings.length) {
    return null;
  }

  return deal.filings.map((filing: IDealFiling, index) => {
    const comma = (index < deal.filings.length - 1) ? ', ' : '';

    return (
      // eslint-disable-next-line react/no-array-index-key
      <Fragment key={ index }>
        <a
          className={ styles.filingsLink }
          href={ filing.url }
          target="_blank"
        >
          { filing.type }
          <IconNewWindow
            className={ styles.filingsIcon }
          />
        </a>
        { comma }
      </Fragment>
    );
  });
};

export const renderPropertyGroups = (propertyGroups) => !!propertyGroups.length && propertyGroups[0].length > 0 && (
  <div className={ styles.propertiesContainer }>
    { propertyGroups.map((propertyGroup, propertyGroupIndex) => (
      <div
        className={ styles.propertyColumn }
        /* eslint-disable-next-line react/no-array-index-key */
        key={ propertyGroupIndex }
      >
        { propertyGroup.map((property, propertyIndex) => (
          <div
            className={ styles.propertyWrapper }
            /* eslint-disable-next-line react/no-array-index-key */
            key={ propertyIndex }
          >
            <div
              className={ cn({
                [styles.even]: propertyIndex % 2 === 0,
            }) }
            />
            <div className={ styles.property }>
              <div className={ styles.propertyLabel }>
                { property.name }
              </div>
              <div className={ styles.propertyValue }>
                { property.value }
              </div>
            </div>
          </div>
        )) }
      </div>
    )) }
  </div>
);

export const mapTrancheComments = (tranche: ITranch | IAbsActiveCalendarTrancheView): string | null => {
  let props = [];

  if (tranche.bookSize) {
    props.push(`Book size: ${ tranche.bookSize };`);
  }
  if (tranche.firstPayDate) {
    props.push(`First Pay: ${ tranche.firstPayDate };`);
  }
  if (tranche.redemption) {
    props.push(`Redemption: ${ tranche.redemption };`);
  }
  if (tranche.registration) {
    props.push(`Registration: ${ tranche.registration.name };`);
  }
  if (tranche.comments) {
    props.push(`Comments: ${ tranche.comments };`);
  }

  if (props.length) {
    // if class is defined - prepend comments with class
    if (tranche.class) {
      props = [`${ tranche.class }:`, ...props];
    }
  }

  return props.join(' ');
};
